import dayjs from 'dayjs';
import cookie from 'js-cookie';
import type { PdpContext } from 'types';
import type { CapiProductPage } from 'types/capi/pdp';
import { fromProductDetailPage } from 'types/TrackableProduct';
import type { User } from 'types/User';
import * as uuid from 'uuid';

import braze from '~/services/braze.client';
import facebook from '~/services/facebook';
import facebookPixel from '~/services/facebook.pixel';
import gtm from '~/services/gtm.client';
import tiktok from '~/services/tiktok.pixel';
import twitter from '~/services/twitter.pixel';
import { getPreviousContext } from '~/utils/pageContext';

import UserEvent from './UserEvent';

type EventData = {
  clientIp: string | null;
  isBot: boolean;
  product: CapiProductPage;
  user: User;
};

export default class PdpViewEvent extends UserEvent<EventData> {
  private readonly eventId = uuid.v4();
  private readonly pdpContext = getPreviousContext<PdpContext>();

  private getProductId() {
    let productId = `${this.data.product.product_id}`;

    if (this.data.product.sub_type === 'clothing') {
      productId += `-${this.data.product.variant_id}`;
    }

    return productId;
  }

  private getPrice() {
    return this.data.product.price.sale ?? this.data.product.price.regular;
  }

  private getCurrency() {
    return this.data.product.price.currency;
  }

  private getNumOfListings() {
    return this.data.product.stock_listings.affiliates.length;
  }

  async sendToFacebook() {
    const eventId = this.eventId;

    await facebookPixel.fireEvent({
      data: {
        content_ids: [this.getProductId()],
        content_type: 'product_group',
        currency: this.getCurrency(),
        value: this.getPrice()
      },
      eventID: eventId,
      eventName: facebookPixel.StandardEventNames.ViewContent
    });

    const hashedProfile = await facebook.getHashedProfile(
      this.data.user.profile
    );

    const serverEventData = [
      {
        action_source: 'website',
        custom_data: {
          brand: this.data.product.brand ?? '',
          content_ids: [this.data.product.product_id],
          content_name: this.data.product.name ?? '',
          content_type: 'product_group',
          currency: this.data.product.price?.currency ?? '',
          value:
            this.data.product.price?.sale ??
            this.data.product.price.regular ??
            0
        },
        event_id: eventId,
        event_name: facebookPixel.StandardEventNames.ViewContent,
        event_source_url: this.data.product.canonical_url,
        event_time: dayjs().unix(),
        user_data: {
          client_ip_address: this.data.clientIp,
          client_user_agent: navigator?.userAgent,
          external_id: this.data.user.id ?? 'guest',
          fbc: cookie.get('_fbc'),
          fbp: cookie.get('_fbp'),
          ...hashedProfile
        }
      }
    ];

    this.serverEvents.push({ data: serverEventData, type: 'facebook' });
  }

  sendToTikTok() {
    return tiktok.fireEvent('ViewContent', {
      content_ids: [this.getProductId()],
      content_type: 'product',
      currency: this.getCurrency(),
      value: this.getPrice()
    });
  }

  collectServerEvents() {
    this.serverEvents.push({
      data: {
        currency: this.getCurrency(),
        event_name: 'pdp_view' as const,
        is_logged_in: this.data.user.isLoggedIn,
        item: {
          ...gtm.Ecommerce.getEcommerceItem(
            fromProductDetailPage(this.data.product),
            this.pdpContext?.position
          )
        },
        num_of_listings: this.getNumOfListings(),
        page_path: window.location.pathname,
        platform: 'web' as const,
        position: this.pdpContext?.position ?? null,
        product_brand: this.data.product.brand,
        product_id: this.data.product.product_id,
        product_view_price: this.getPrice(),
        stock_status: this.data.product.stock_status,
        style_code: this.data.product.style_code,
        surface: 'pdp' as const,
        surface_source: this.pdpContext?.surface_source ?? 'direct',
        user_id: this.data.user.id || null,
        value: this.getPrice()
      },
      type: 'pdp_view' as const
    });
  }

  sendToTwitter() {
    return twitter.fireEvent(twitter.EventIds.ViewContent, {
      contents: [{ content_id: this.getProductId(), content_type: 'product' }],
      currency: this.getCurrency(),
      value: this.getPrice()
    });
  }

  sendToGtm() {
    return gtm.Ecommerce.viewListItem(
      this.data.product,
      this.pdpContext?.position
    );
  }

  sendToBraze(): void {
    return braze.logPdpViewEvent(this.data.product._event_data.marketing);
  }
}
